import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { FileDto } from './file';

export type ChatContextType = 'REQUEST' | 'DEAL';

export interface ChatMessageSender {
  id: number;
  fullName: string;
  companyName?: string;
  roleCode?: string;
}

export interface ChatMessage {
  id: number;
  contextType: ChatContextType;
  contextId: number;
  sender: ChatMessageSender;
  body: string | null;
  deleted: boolean;
  edited: boolean;
  editableNow: boolean;
  replyToMessageId?: number;
  replyToSender?: ChatMessageSender;
  replyToSnippet?: string;
  attachments: FileDto[];
  createdAt: string;
  editedAt?: string;
}

export interface ChatMessagePage {
  content: ChatMessage[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
}

/** Один приватный тред переписки владельца заявки с конкретным экспедитором. */
export interface ChatThread {
  forwarderId: number;
  /** Анонимная метка («Экспедитор N») владельцу, реальная компания брокеру. */
  label: string;
  lastMessageSnippet?: string;
  lastMessageAt: string;
}

const API_URL = '/cargo/api/chat';

/**
 * Чат заявки — отдельный приватный тред владелец↔каждый экспедитор (forwarderId; у
 * экспедитора всегда свой единственный тред, forwarderId не передаётся и игнорируется
 * бэкендом) — и чат сделки (приватно, владелец + экспедитор сделки, один тред) — см.
 * ChatController.java. Один и тот же сервис/компонент используется на всех страницах,
 * разница — contextType и (для REQUEST у владельца/брокера) forwarderId.
 */
@Injectable({
  providedIn: 'root'
})
export class ChatService {
  constructor(private http: HttpClient) {}

  getMessages(
    contextType: ChatContextType, contextId: number, page = 0, size = 50, forwarderId?: number
  ): Observable<ChatMessagePage> {
    const params: Record<string, string | number> = { page, size, sort: 'id,asc' };
    if (forwarderId != null) params['forwarderId'] = forwarderId;
    return this.http.get<ChatMessagePage>(`${API_URL}/${contextType}/${contextId}/messages`, { params });
  }

  /** Треды заявки — с какими экспедиторами уже есть переписка (владельцу и брокеру). */
  getThreads(requestId: number): Observable<ChatThread[]> {
    return this.http.get<ChatThread[]>(`${API_URL}/REQUEST/${requestId}/threads`);
  }

  sendMessage(
    contextType: ChatContextType,
    contextId: number,
    body: string | null,
    replyToMessageId?: number,
    files?: File[],
    forwarderId?: number
  ): Observable<ChatMessage> {
    const formData = new FormData();
    if (body) formData.append('body', body);
    if (replyToMessageId != null) formData.append('replyToMessageId', replyToMessageId.toString());
    if (files) files.forEach(f => formData.append('files', f));
    if (forwarderId != null) formData.append('forwarderId', forwarderId.toString());
    return this.http.post<ChatMessage>(`${API_URL}/${contextType}/${contextId}/messages`, formData);
  }

  editMessage(messageId: number, body: string): Observable<ChatMessage> {
    return this.http.patch<ChatMessage>(`${API_URL}/messages/${messageId}`, { body });
  }

  deleteMessage(messageId: number): Observable<void> {
    return this.http.delete<void>(`${API_URL}/messages/${messageId}`);
  }
}
